import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import useSortableTable from "../../hooks/useSortableTable";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import RowActionsMenu from "../UI/RowActionsMenu";
import EmptyState from "../UI/EmptyState";
import SearchBar from "../UI/SearchBar";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import EditTeamDialog from "./EditTeamDialog";
import DeleteTeamDialog from "./DeleteTeamDialog";

const TeamTable = (props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [search, setSearch] = useState("");

  const rows = props.teams.map(({ id, name, manager }) => ({
    id,
    name,
    manager: manager.user.username,
  }));

  const teamsWithPermission = props.teamsWithPermission.map((t) => t.id);

  const { sorted: sortedRows, sortKey, sortDir, handleSort } = useSortableTable(rows, "name");

  if (rows.length === 0) {
    return <EmptyState icon={<GroupsRoundedIcon />} message="No teams found." />;
  }

  const q = search.toLowerCase();
  const sorted = sortedRows.filter(
    (row) =>
      row.name.toLowerCase().includes(q) ||
      row.manager.toLowerCase().includes(q)
  );

  const noResults = sorted.length === 0;

  return (
    <>
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search teams or managers..."
      />

      {noResults ? (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          No teams match &ldquo;{search}&rdquo;.
        </Typography>
      ) : isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {sorted.map((row) => (
            <Card
              key={row.id}
              data-testid={"teamTableRow" + row.id}
              sx={{ cursor: "pointer", backgroundColor: "#2a3447" }}
              onClick={() => navigate(`/team/${row.id}`)}
            >
              <CardContent sx={{ pb: "12px !important" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {row.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Manager: {row.manager}
                    </Typography>
                  </Box>
                  {teamsWithPermission.includes(row.id) && (
                    <RowActionsMenu
                      onEdit={() => setEditRow(row)}
                      onDelete={() => setDeleteRow(row)}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer
          className="tableContainer"
          data-testid="teamTable"
          component={Paper}
          sx={{ overflowX: "auto" }}
        >
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                {teamsWithPermission.length > 0 && <TableCell sx={{ width: 48, p: 0.5 }} />}
                <TableCell sortDirection={sortKey === "name" ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === "name"}
                    direction={sortKey === "name" ? sortDir : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortKey === "manager" ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === "manager"}
                    direction={sortKey === "manager" ? sortDir : "asc"}
                    onClick={() => handleSort("manager")}
                  >
                    Manager
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((row) => (
                <TableRow
                  data-testid={"teamTableRow" + row.id}
                  hover
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}
                  onClick={() => navigate(`/team/${row.id}`)}
                >
                  {teamsWithPermission.length > 0 && (
                    <TableCell sx={{ width: 48, p: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      {teamsWithPermission.includes(row.id) && (
                        <RowActionsMenu
                          onEdit={() => setEditRow(row)}
                          onDelete={() => setDeleteRow(row)}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.manager}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editRow && (
        <EditTeamDialog
          open
          setOpen={(open) => !open && setEditRow(null)}
          team={editRow}
          managers={props.managers}
        />
      )}
      {deleteRow && (
        <DeleteTeamDialog
          open
          setOpen={(open) => !open && setDeleteRow(null)}
          team={deleteRow}
        />
      )}
    </>
  );
};

export default TeamTable;
